import type { EntitlementStatus, EnforcementState } from './types';

export type BannerTone = 'none' | 'info' | 'warning' | 'restricted' | 'locked';

export interface StatePresentation {
  readonly tone: BannerTone;
  readonly title: string;
  /** Plain-language reason; the backend remediation (recovery path) is shown separately and always. */
  readonly message: string;
  /** True when the deployment still permits full function (Licensed/Grace). */
  readonly fullFunction: boolean;
}

/** Human label for a diagnostic kind, so the banner reads in plain language rather than a machine token. */
function reasonFor(kind: string): string {
  switch (kind) {
    case 'no_license':
      return 'No license is installed.';
    case 'integrity_failure':
      return 'The installed license could not be verified (signature or tamper).';
    case 'revoked':
      return 'This license has been revoked.';
    case 'fingerprint_unbound':
      return 'This deployment is no longer bound to its activation (the hardware changed beyond tolerance).';
    case 'catalogue_skew':
      return 'The license and product catalogue versions do not match.';
    case 'boot_failure':
      return 'The licensing subsystem could not start safely.';
    case 'degraded':
      // M9-7: 'degraded' has several causes; the expiry sentence was shown for ALL of them,
      // telling heartbeat-grace users their license had expired. Say what is actually known.
      return 'This deployment is in its grace period.';
    case 'limit_exceeded':
      return 'This deployment exceeds its licensed capacity.';
    default:
      return '';
  }
}

/** Plain-language name for a limit resource key, for the capacity lock message. */
function resourceLabel(resource: string): string {
  switch (resource) {
    case 'limit.projects': return 'projects';
    case 'limit.assets': return 'assets';
    case 'limit.seats': return 'users';
    case 'limit.assessmentRunsPerPeriod': return 'assessment runs this month';
    default: return resource.replace(/^limit\./, '');
  }
}

/**
 * The four-state UI vocabulary (Appendix D "UI signal" column), in one place. Licensed renders nothing;
 * Grace is a persistent warning with full function; Restricted is a read-only signal; Locked blocks
 * functional access. The plain-language reason is derived from the diagnostic kind; the recovery path is
 * the backend `remediation`, surfaced alongside so the UI never dead-ends (UIX-15).
 */
export function presentState(status: EntitlementStatus): StatePresentation {
  const reason = reasonFor(status.kind);
  // M10-LIMITS — a limit lock (state 'locked', kind 'limit_exceeded') shows a capacity-specific message
  // naming the resource and the numbers, instead of the generic/ revoke locked copy. Same lock behaviour,
  // clearer story: the customer sees exactly what to expand.
  const lockedMessage =
    status.kind === 'limit_exceeded' && status.limitExceeded
      ? `This deployment is using ${status.limitExceeded.current} ${resourceLabel(status.limitExceeded.resource)} ` +
        `but its license permits ${status.limitExceeded.limit}. Functional access is blocked until you install a ` +
        `license that covers your usage. Your data is preserved and recoverable. Contact AvinyaSQ to expand your license.`
      : `${reason} Functional access is blocked. Your data is preserved and recoverable.`.trim();
  const map: Record<EnforcementState, StatePresentation> = {
    licensed: { tone: 'none', title: 'Licensed', message: '', fullFunction: true },
    grace: {
      tone: 'warning',
      title: 'License in grace period',
      message: reason || 'Your license needs attention. Full functionality continues for now.',
      fullFunction: true,
    },
    restricted: {
      tone: 'restricted',
      title: 'Read-only mode',
      message: `${reason} New runs, imports and changes are blocked. Existing data stays viewable and exportable.`.trim(),
      fullFunction: false,
    },
    locked: {
      tone: 'locked',
      title: status.kind === 'limit_exceeded' ? 'License capacity exceeded' : 'Licensing locked',
      message: lockedMessage,
      fullFunction: false,
    },
  };
  return map[status.state];
}

/** Recovery affordance for a Locked deployment — distinguishes a stale BINDING (re-host) from a missing or
 *  invalid LICENSE (install). Under (c), `fingerprint_unbound` is a routine operator event (re-image,
 *  board swap, clone) whose fix is a signed re-host, NOT re-licensing — so the Locked screen must not
 *  dead-end it behind an "install a license" CTA (UIX-15). */
export type RecoveryKind = 'rehost' | 'license';
export interface Recovery {
  readonly kind: RecoveryKind;
  readonly adminCta: string;
  readonly contactText: string;
}
export function recoveryFor(status: EntitlementStatus): Recovery {
  if (status.kind === 'fingerprint_unbound') {
    return {
      kind: 'rehost',
      adminCta: 'Start re-host',
      contactText: 'Contact an administrator to re-host this deployment onto its current hardware and restore access.',
    };
  }
  return {
    kind: 'license',
    adminCta: 'Install a valid license',
    contactText: 'Contact an administrator to install a valid license and restore access.',
  };
}
